/**
 * Deleting a blueprint (D-25), in its two forms.
 *
 *   Soft   recoverable. Moves it to the archive; versions, content and grants stay
 *          exactly where they are. Offered wherever a blueprint is listed.
 *   Hard   irreversible. Offered ONLY from the archive, so destroying something
 *          is always a second, separate decision after removing it from view.
 *          The API requires `confirm` to equal the blueprint's own slug. The
 *          field is empty and stays empty until the person types it: the point
 *          is that they have looked at what they are destroying, and a prefilled
 *          box would defeat it.
 *
 * Both dialogues name the blueprint AND its client, since two clients can hold
 * blueprints of the same name.
 */
import { useEffect, useState, type FormEvent } from 'react';
import { useTerms } from '../../lib/terms';
import {
  errorText,
  useHardDeleteBlueprint,
  useSoftDeleteBlueprint,
} from '../../lib/admin';
import { Dialog } from '../versions/Dialog';
import { ConfirmDialog, FormError } from './ui';

export interface DeletionTarget {
  id: string;
  name: string;
  slug: string;
  clientName: string;
}

export function SoftDeleteDialog({
  target,
  onClose,
  onDone,
}: {
  target: DeletionTarget | null;
  onClose: () => void;
  onDone: (target: DeletionTarget) => void;
}) {
  const terms = useTerms();
  const del = useSoftDeleteBlueprint();
  const { reset } = del;
  useEffect(() => {
    if (target === null) reset();
  }, [target, reset]);

  return (
    <ConfirmDialog
      open={target !== null}
      title={target ? `Delete ‘${target.name}’?` : ''}
      confirmLabel={target ? `Delete ‘${target.name}’` : ''}
      danger
      busy={del.isPending}
      error={del.isError ? errorText(del.error) : null}
      onClose={onClose}
      onConfirm={() => {
        if (target) del.mutate(target.id, { onSuccess: () => onDone(target) });
      }}
      body={
        target ? (
          <>
            <p>
              <b>{target.name}</b> ({target.clientName}) will disappear for everyone, administrators
              included, and nobody will be able to open it.
            </p>
            <p>
              Nothing is destroyed. It moves to the archive with its versions, content and access
              grants intact, and an administrator can restore it from there. Its slug stays
              reserved, so a new {terms.noun('blueprint')} cannot reuse it until this one is restored
              or permanently deleted.
            </p>
          </>
        ) : null
      }
    />
  );
}

export function HardDeleteDialog({
  target,
  onClose,
  onDone,
}: {
  target: DeletionTarget | null;
  onClose: () => void;
  onDone: (target: DeletionTarget) => void;
}) {
  const del = useHardDeleteBlueprint();
  const { reset } = del;
  useEffect(() => {
    if (target === null) reset();
  }, [target, reset]);

  return (
    <Dialog
      open={target !== null}
      title={target ? `Permanently delete ‘${target.name}’?` : ''}
      onClose={onClose}
      dismissible={!del.isPending}
    >
      {target ? <HardDeleteForm target={target} del={del} onClose={onClose} onDone={onDone} /> : null}
    </Dialog>
  );
}

function HardDeleteForm({
  target,
  del,
  onClose,
  onDone,
}: {
  target: DeletionTarget;
  del: ReturnType<typeof useHardDeleteBlueprint>;
  onClose: () => void;
  onDone: (target: DeletionTarget) => void;
}) {
  // Deliberately starts empty. See the file header.
  const [typed, setTyped] = useState('');
  const matches = typed === target.slug;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!matches || del.isPending) return;
    del.mutate({ id: target.id, confirm: typed }, { onSuccess: () => onDone(target) });
  };

  return (
    <form className="bp-dialog-form" onSubmit={submit} noValidate>
      <p>
        <b>{target.name}</b> ({target.clientName}) will be destroyed, together with every version,
        all plan content, every upload record and every access grant. <b>This cannot be undone</b>{' '}
        and there is no way to bring it back.
      </p>
      {/* The slug sits OUTSIDE .field-label, which upper-cases its text: the
          person must type it exactly, and it has to look exactly as it must be typed. */}
      <p>
        To confirm, type its slug, <code className="adm-code">{target.slug}</code>, in the box below.
      </p>
      <label className="field">
        <span className="field-label">Slug</span>
        <input
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          autoComplete="off"
          autoCapitalize="off"
          spellCheck={false}
          aria-label={`Type ${target.slug} to confirm`}
        />
      </label>
      {del.isError ? <FormError>{errorText(del.error)}</FormError> : null}
      <div className="bp-dialog-actions">
        <button type="button" className="btn" onClick={onClose} disabled={del.isPending}>
          Cancel
        </button>
        <button type="submit" className="btn danger" disabled={!matches || del.isPending}>
          {del.isPending ? 'Deleting…' : `Permanently delete ‘${target.name}’`}
        </button>
      </div>
    </form>
  );
}
